import React, { forwardRef, ReactElement, RefAttributes } from 'react';
import styled, { css, DefaultTheme, useTheme } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { PxToRem, SIZES } from '../../fundamentals/sizes/sizes';
import { EoraSpace, IGutterProps } from '../../fundamentals/spaces/space';
import { ButtonRoot, IButtonRoot } from './ButtonRoot';
import { IDisplayType, INoLineHeightProps } from '../../fundamentals/typography/typography';
import { ITheme } from '../../themes/theme';
import { DEFAULT_ICON_TYPE, Icon, IconSize } from '../Icon/Icon';
import { ButtonGroupContext } from './ButtonGroupContext';
import { resolveProps } from '../../common/utils';
import { BUTTON_MODES, BUTTON_VARIANTS, ButtonMode, ButtonVariant, LINK_VARIANTS, LinkVariant } from './buttonVariants';
import { IFullWidthProps, useFullWidthOnSmallCss } from '../../fundamentals/breakpoints/breakpoints';
import { LoadingAnimation, LoadingAnimationSize } from '../LoadingAnimation/LoadingAnimation';
import { useFilterColourCss } from '../../fundamentals/colours/filter';
import { IEndIconBaseProps, IStartIconBaseProps } from '../../common/interfaces';
import { ColourAssignment, ColourState } from '../../fundamentals/colours/colourInterfaces';
import { DEFAULT_DISABLED_STATE, IStateSelectorProps } from '../../fundamentals/colours/variants';
import { Badge, BadgeSize, IBadgeProps } from '../Badge/Badge';
import { ITypographyCss, useEoraCss } from '../../fundamentals/typography/typographyEora';
import { TYPOGRAPHY } from '../../fundamentals/typography/eoraTypography';

export type ButtonSize = typeof SIZES.X_SMALL | typeof SIZES.SMALL | typeof SIZES.MEDIUM | typeof SIZES.LARGE;

export interface IBaseButtonProps extends Omit<React.HTMLProps<HTMLButtonElement>, 'size' | 'ref' | 'type' | 'as'>, IButtonRoot {
  disabled?: boolean;
  keepStyleOnDisabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  as?: 'button' | 'submit' | 'reset';
}

export interface IButtonProps extends IBaseButtonProps, IGutterProps, IFullWidthProps, IStartIconBaseProps, IEndIconBaseProps {
  size?: ButtonSize;
  selected?: boolean; // To be used in ButtonGroup, Pagination
  variant?: ButtonVariant;
  constantWidth?: boolean;
  processing?: boolean;
  mode?: ButtonMode; // cannot use style as param

  // Note: assuming that buttons only accept badgeLabels and not the extra icons
  badgeLabel?: IBadgeProps['label'];
  badgeVariant?: IBadgeProps['variant'];
}

const buttonPads = (eoraSpaces: Record<EoraSpace, PxToRem>): Record<ButtonSize, string> => ({
  [SIZES.X_SMALL]: `${eoraSpaces.xsmall} ${eoraSpaces.offgrid_12}`,
  [SIZES.SMALL]: `${eoraSpaces.offgrid_6} ${eoraSpaces.large}`,
  [SIZES.MEDIUM]: `${eoraSpaces.medium} ${eoraSpaces.offgrid_20}`,
  [SIZES.LARGE]: `${eoraSpaces.offgrid_10} ${eoraSpaces.xlarge}`
});

export const buttonFontSizes: Record<ButtonSize, { name: ITypographyCss; lineHeight: number }> = {
  [SIZES.X_SMALL]: { name: TYPOGRAPHY.PARAGRAPH.MICRO.MEDIUM, lineHeight: 20 },
  [SIZES.SMALL]: { name: TYPOGRAPHY.PARAGRAPH.SMALL.MEDIUM, lineHeight: 20 },
  [SIZES.MEDIUM]: { name: TYPOGRAPHY.PARAGRAPH.DEFAULT.MEDIUM, lineHeight: 24 },
  [SIZES.LARGE]: { name: TYPOGRAPHY.PARAGRAPH.LARGE.MEDIUM, lineHeight: 28 }
};

export const buttonIconSizes: Record<ButtonSize, IconSize> = {
  [SIZES.X_SMALL]: SIZES.MICRO,
  [SIZES.SMALL]: SIZES.XXX_SMALL,
  [SIZES.MEDIUM]: SIZES.X_SMALL,
  [SIZES.LARGE]: SIZES.X_SMALL
};

const buttonGaps = (eoraSpaces: Record<EoraSpace, PxToRem>): Record<ButtonSize, string> => ({
  [SIZES.X_SMALL]: eoraSpaces.offgrid_6,
  [SIZES.SMALL]: eoraSpaces.medium,
  [SIZES.MEDIUM]: eoraSpaces.offgrid_10,
  [SIZES.LARGE]: eoraSpaces.offgrid_12
});

const badgeSizes: Record<ButtonSize, BadgeSize> = {
  [SIZES.X_SMALL]: SIZES.MICRO,
  [SIZES.SMALL]: SIZES.XXX_SMALL,
  [SIZES.MEDIUM]: SIZES.XX_SMALL,
  [SIZES.LARGE]: SIZES.X_SMALL
};

// eslint-disable-next-line unicorn/prefer-set-has
export const variantsThatAllowProcessing: CommonButtonVariant[] = [
  BUTTON_VARIANTS.PRIMARY,
  BUTTON_VARIANTS.SECONDARY,
  BUTTON_VARIANTS.ACCENT,
  BUTTON_VARIANTS.ERROR,
  BUTTON_VARIANTS.WARNING,
  BUTTON_VARIANTS.SUCCESS
];

export const DEFAULT_BUTTON_VARIANT = BUTTON_VARIANTS.SECONDARY;
export const DEFAULT_BUTTON_MODE = BUTTON_MODES.BOLD;
export const DEFAULT_GROUP_BUTTON_KIND = BUTTON_MODES.OUTLINE;
export const DEFAULT_BUTTON_SIZE = SIZES.SMALL;

const getVariantSelector = (variant: ButtonVariant, styleMode: ButtonMode, theme: ITheme & DefaultTheme, selector?: keyof ColourState) => {
  const modeSelector: ColourAssignment | undefined = selector ? theme.buttonColours[variant!][styleMode][selector] : theme.buttonColours[variant!][styleMode];

  const variantSelector: ColourAssignment | undefined = selector
    ? theme.buttonColours[variant!][DEFAULT_BUTTON_MODE][selector]
    : theme.buttonColours[variant!][DEFAULT_BUTTON_MODE];

  const defaultVariantSelector = selector
    ? theme.buttonColours[DEFAULT_BUTTON_VARIANT][DEFAULT_BUTTON_MODE][selector]
    : theme.buttonColours[DEFAULT_BUTTON_VARIANT][DEFAULT_BUTTON_MODE];

  return modeSelector ?? variantSelector ?? defaultVariantSelector;
};

type IButtonElementExtraProps = Pick<IStateSelectorProps, 'cssState'> & { bare?: boolean };

interface IButtonStyleProps
  extends Pick<IButtonProps, 'size' | 'variant' | 'disabled' | 'fullWidth' | 'selected' | 'constantWidth' | 'keepStyleOnDisabled'>,
    IButtonElementExtraProps {
  styleMode?: ButtonMode; // use other name to avoid being automatically added to rendered Button
}

export type ButtonSelectorCssProps = Pick<IButtonStyleProps, 'variant' | 'styleMode'> & Pick<IButtonProps, 'mode'>;
export const useButtonSelectorCss = (selector?: keyof ColourState) => css<ButtonSelectorCssProps>`
  ${({ variant, styleMode, theme, mode }) => {
    const currentSelector = getVariantSelector(variant!, styleMode ?? mode!, theme, selector);
    return css`
      ${currentSelector?.text && `color: ${currentSelector.text}`};
      ${currentSelector?.background && `background: ${currentSelector.background}`};
      ${currentSelector?.textDecoration && `text-decoration: ${currentSelector.textDecoration}`};
      ${currentSelector?.border && `border: ${currentSelector.border}`};
      ${currentSelector?.opacity && `opacity: ${currentSelector.opacity}`};
    `;
  }}
`;

export type IconCssProps = Pick<IButtonStyleProps, 'variant' | 'styleMode'>;
const useIconCss = (selector?: keyof ColourState) => css<IconCssProps>`
  ${({ variant, styleMode, theme }) => css`
    ${Icon} {
      ${useFilterColourCss(getVariantSelector(variant!, styleMode!, theme, selector)?.text ?? getVariantSelector(variant!, styleMode!, theme)?.text)};
    }
  `}
`;

export type ButtonCssProps = Pick<IButtonStyleProps, 'disabled' | 'keepStyleOnDisabled' | 'styleMode' | 'selected' | 'cssState'> &
  ButtonSelectorCssProps &
  IconCssProps;
export const useButtonCss = css<ButtonCssProps>`
  ${({ cssState }) => useButtonSelectorCss(cssState)};

  ${({ disabled, keepStyleOnDisabled }) => {
    if (disabled && keepStyleOnDisabled) {
      return css``;
    }

    if (disabled) {
      return css`
        ${useButtonSelectorCss(`disabled`)}
      `;
    }

    return css`
      &:focus {
        ${useButtonSelectorCss(`focus`)};
        ${useIconCss(`focus`)};
      }

      &:hover {
        ${useButtonSelectorCss(`hover`)};
        ${useIconCss(`hover`)};
      }

      &:active {
        ${useButtonSelectorCss(`active`)};
        ${useIconCss(`active`)};
      }
    `;
  }}
`;

export const useButtonHeightCss = css<Pick<IButtonProps, 'size'>>`
  height: ${({ theme, size }) => theme.sizes[size!]};
`;

export const isButtonLinkVariant = (variant: ButtonVariant) => Object.values(LINK_VARIANTS).includes(variant as LinkVariant);

const useButtonBaseCss = css<IButtonStyleProps>`
  ${({ variant, size, theme, bare }) =>
    !isButtonLinkVariant(variant!) &&
    !bare &&
    css`
      border-radius: ${theme.buttonTabBadgeBorderRadius[size!]};
    `};

  ${({ bare, theme, variant, disabled, selected }) =>
    bare &&
    css`
      ${useButtonHeightCss};
      color: ${selected ? theme.buttonColours.black.bold.background : theme.buttonColours.linkDark.bold.text};
      ${Icon} {
        ${useFilterColourCss(theme.buttonColours.black.bold.background)};
      }

      ${() => {
        if (disabled) {
          return css`
            ${DEFAULT_DISABLED_STATE}
          `;
        }

        return css`
          &:hover {
            color: ${theme.buttonColours.black.bold.background};
            border-bottom-color: ${theme.buttonColours[variant!].bold.background};
          }
          &:active {
            color: ${theme.buttonColours.black.bold.background};
            border-bottom-color: ${theme.buttonColours[variant!].bold.background};
          }
        `;
      }}
    `}
`;

const BUTTON_FOCUS_INDEX = 1;

const useButtonOnGroupCss = css<IButtonStyleProps>`
  &:first-child {
    border-top-left-radius: ${({ theme, size }) => theme.buttonTabBadgeBorderRadius[size!]};
    border-bottom-left-radius: ${({ theme, size }) => theme.buttonTabBadgeBorderRadius[size!]};
  }

  &:last-child {
    border-top-right-radius: ${({ theme, size }) => theme.buttonTabBadgeBorderRadius[size!]};
    border-bottom-right-radius: ${({ theme, size }) => theme.buttonTabBadgeBorderRadius[size!]};
  }

  &:not(:last-child):not(:focus),
  &:hover:not(:last-child):not(:focus),
  &:active:not(:last-child):not(:focus) {
    border-right: 0;
  }

  flex: 1 1 auto;

  &:focus + & {
    border-left: 0;
  }

  &:focus {
    z-index: ${BUTTON_FOCUS_INDEX};
  }

  ${({ selected }) =>
    selected &&
    css`
      ${({ theme }) => {
        // Button primary, mode outline will always have border key
        const borderSize = theme.buttonColours[DEFAULT_BUTTON_VARIANT][DEFAULT_GROUP_BUTTON_KIND].border!.split(' ');
        return css`
          border: ${`${borderSize[0]} ${borderSize[1]} transparent`};
        `;
      }}

      &:not(:focus) {
        z-index: ${BUTTON_FOCUS_INDEX - 1};
      }
    `};
`;

export type CommonButtonBaseCssProps = Pick<IButtonProps, 'size'> & IDisplayType & INoLineHeightProps;
export const useCommonButtonBaseCss = css<CommonButtonBaseCssProps>`
  column-gap: ${({ size, theme }) => buttonGaps(theme.eoraSpaces)[size!]};
  ${({ size }) => useEoraCss(buttonFontSizes[size!].name, false, buttonFontSizes[size!].lineHeight)}
`;

export const useButtonSpacingCss = css<Pick<IButtonProps, 'size'>>`
  ${useButtonHeightCss};
  padding: ${({ size, theme }) => buttonPads(theme.eoraSpaces)[size!]};
`;

export type ButtonBaseProps = IButtonStyleProps & { isGroup?: boolean } & CommonButtonBaseCssProps & IFullWidthProps & ButtonCssProps;
const ButtonBase = styled(ButtonRoot)<ButtonBaseProps>`
  ${({ isGroup }) => (isGroup ? useButtonOnGroupCss : useButtonBaseCss)};
  ${({ constantWidth }) => !constantWidth && useFullWidthOnSmallCss};
  ${({ variant, bare }) => !isButtonLinkVariant(variant!) && !bare && useButtonSpacingCss};

  ${useCommonButtonBaseCss};
  ${({ bare }) => !bare && useButtonCss};
`;

export const getIconColour = ({ variant, mode, disabled }: Required<Pick<IButtonProps, 'variant' | 'mode'>> & Pick<IButtonProps, 'disabled'>) => {
  const theme = useTheme();
  return disabled ? (theme.buttonColours[variant][mode].disabled?.text ?? theme.buttonColours[variant][mode].text) : theme.buttonColours[variant][mode].text;
};

export const processingAllowed = (variant: CommonButtonVariant, buttonMode: ButtonMode) =>
  variantsThatAllowProcessing.includes(variant) && buttonMode === BUTTON_MODES.BOLD;

type ButtonElementsProps = ICommonButtonProps & IButtonElementExtraProps;

export const ButtonElement = forwardRef<HTMLButtonElement, ButtonElementsProps>((props, ref) => {
  const contextProps = React.useContext(ButtonGroupContext);
  const resolvedProps = resolveProps({ ...contextProps }, { ...props });

  const {
    children,
    size = DEFAULT_BUTTON_SIZE,
    variant = DEFAULT_BUTTON_VARIANT,
    disabled,
    keepStyleOnDisabled,
    startIcon,
    endIcon,
    startIconType = DEFAULT_ICON_TYPE,
    endIconType = DEFAULT_ICON_TYPE,
    onClick,
    mode = DEFAULT_BUTTON_MODE,
    processing,
    badgeLabel,
    badgeVariant,
    type = 'button',
    ...rest
  } = resolvedProps;

  const { generateTestId } = useTestId(rest, `Button`);

  const colour = getIconColour({ variant: variant, mode: mode, disabled });

  const appliedVariant = rest.isGroup && rest.selected ? BUTTON_VARIANTS.PRIMARY : variant;
  const appliedKind = rest.isGroup && !rest.selected ? BUTTON_MODES.OUTLINE : mode;

  const handleOnclick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    e.currentTarget.blur();
  };

  const showProcessing = processingAllowed(variant, mode) && processing;
  const spinnerCustomVariant = variant === 'secondary' ? undefined : colour;

  return (
    <ButtonBase
      ref={ref}
      {...generateTestId(variant)}
      size={size}
      variant={appliedVariant}
      disabled={disabled || (disabled === undefined && showProcessing)}
      onClick={onClick && handleOnclick}
      type={type}
      styleMode={appliedKind}
      keepStyleOnDisabled={keepStyleOnDisabled}
      {...rest}
    >
      {showProcessing && (
        <LoadingAnimation
          size={buttonIconSizes[size] as LoadingAnimationSize}
          variant='neutral'
          customVariant={spinnerCustomVariant}
          {...generateTestId(`processing`)}
        />
      )}
      {!!startIcon && !showProcessing && (
        <Icon colour={colour} size={buttonIconSizes[size]} type={startIconType} name={startIcon} {...generateTestId(`startIcon`)} />
      )}
      {children}
      {!!endIcon && <Icon colour={colour} size={buttonIconSizes[size]} type={endIconType} name={endIcon} {...generateTestId(`endIcon`)} />}
      {!!badgeLabel && (
        // TODO: map variant when <Badge /> is updated with new colours
        <Badge label={badgeLabel} size={badgeSizes[size!]} variant={badgeVariant} {...generateTestId(`badge`)} />
      )}
    </ButtonBase>
  );
}) as (props: ButtonElementsProps & RefAttributes<HTMLButtonElement>) => ReactElement<HTMLButtonElement>;

export type CommonButtonVariant = Exclude<ButtonVariant, 'white50' | 'black50' | 'white50NoSwitch' | 'black50NoSwitch' | 'brandAware'>;

export interface ICommonButtonProps extends Exclude<IButtonProps, 'variant'> {
  variant?: CommonButtonVariant;
}

export const Button = styled(ButtonElement).attrs<ICommonButtonProps>((args) => ({ bare: false, ...args }))<ICommonButtonProps>``;
